import React, { useEffect } from 'react';
import { InputLabel, Stack, FormHelperText, Select } from '@mui/material';
import { Field, FieldProps } from 'formik';
import { Tooltip } from 'components/elements';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import Autocomplete from './Autocomplete';

interface FormFieldProps {
  name: string;
  label?: string;
  infoToolTip?: string;
  component: React.ComponentType<any>;
  error?: string | false;
  info?: boolean;
  children?: React.ReactNode;
  required?: boolean;
  hidden?: boolean;
  visible?: boolean;
  inputType?: string; // Add inputType for TextArea
  type?: string;
  reserveSpaceForErrorMessage?: boolean;
  [key: string]: any;
}

const FormField: React.FC<FormFieldProps> = ({
  name,
  label,
  infoToolTip,
  hidden,
  visible = true,
  component,
  error,
  info,
  required,
  children,
  inputType,
  reserveSpaceForErrorMessage = false,
  onChange,
  type,
  ...props
}) => {
  const defaultClass = 'form-input-field';

  // Adjust textarea size dynamically
  useEffect(() => {
    if (inputType === 'textarea') {
      const textarea = document.querySelector(`textarea[name="${name}"]`) as HTMLTextAreaElement;
      if (textarea) {
        textarea.style.overflowY = 'auto'; // Ensure scroll is visible
        textarea.style.resize = 'vertical'; // Allow resizing by the user
        textarea.rows = 2; // Default rows
        textarea.maxLength = 500; // Optional: Set maximum length
      }
    }
  }, [name, inputType]);

  if (!visible) {
    return null;
  }

  return (
    <Stack style={hidden ? { display: 'none' } : undefined}>
      {label && (
        <InputLabel className="form-input-label">
          <span>
            {label}
            {required ? ' *' : ''}
          </span>
          {info && (
            <Tooltip title={infoToolTip || 'ToolTip'}>
              <InfoOutlinedIcon fontSize="small" className="info-icon" />
            </Tooltip>
          )}
        </InputLabel>
      )}
      <Field name={name}>
        {({ field, form }: FieldProps) =>
          React.createElement(
            component,
            {
              ...field,
              error: !!error,
              onChange: (event: React.ChangeEvent<any>, option: any, fieldType: string) => {
                if (onChange) {
                  if (component === Autocomplete) {
                    onChange(option);
                  } else {
                    onChange(event, form.setFieldValue);
                  }
                } else {
                  if (name === 'occursAt') {
                    field.onChange(event);
                  } else if (component === Autocomplete) {
                    form.setFieldValue(name, option);
                  } else {
                    if (fieldType === 'selectOption') {
                      form.setFieldValue(name, option?.label);
                    } else if (fieldType === 'clear') {
                      form.setFieldValue(name, '');
                    } else {
                      field.onChange(event);
                    }
                  }
                }
              },
              ...(component === Select
                ? {
                    MenuProps: {
                      PaperProps: {
                        style: {
                          maxHeight: 380,
                          overflowY: 'auto'
                        }
                      }
                    }
                  }
                : {}),
              ...(inputType === 'textarea' ? { multiline: true } : {}),
              ...props,
              className: `${defaultClass} ${name} ${props.className || ''} ${error ? 'error' : ''}`,
              ...(component === Autocomplete
                ? {
                    value: props.multiple ? field.value || [] : field.value
                  }
                : {})
            },
            children
          )
        }
      </Field>
      {(error || reserveSpaceForErrorMessage) && (
        <FormHelperText className={`${reserveSpaceForErrorMessage ? 'reserve-space' : ''}`} error>
          {error}
        </FormHelperText>
      )}
    </Stack>
  );
};

export default FormField;
